import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  LibraryBooks as CourseIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://';

const CourseManagement = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [addCourseLoading, setAddCourseLoading] = useState(false);
  const [editCourseLoading, setEditCourseLoading] = useState(false);
  const [deleteCourseLoading, setDeleteCourseLoading] = useState(false);
  const [courseForm, setCourseForm] = useState({
    name: ''
  });
  const [errors, setErrors] = useState({});
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalCourses, setTotalCourses] = useState(0);

  const fetchCourses = useCallback(async () => {
    try {
      const params = {
        page: paginationModel.page + 1, // Backend uses 1-based indexing
        limit: paginationModel.pageSize,
        ...(searchTerm && { search: searchTerm })
      };
      
      const response = await axios.get(`${API_URL}/courses`, { params });
      setCourses(response.data.courses || []);
      setTotalCourses(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
      setTotalCourses(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, searchTerm]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!courseForm.name.trim()) {
      newErrors.name = 'Course name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddCourse = async () => {
    if (!validateForm()) {
      return;
    }
    
    setAddCourseLoading(true);
    
    try {
      await axios.post(`${API_URL}/courses`, courseForm);
      showSnackbar('Course added successfully!', 'success');
      setAddDialogOpen(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add course';
      showSnackbar(errorMessage, 'error');
    } finally {
      setAddCourseLoading(false);
    }
  };

  const handleEditCourse = async () => {
    if (!validateForm()) {
      return;
    }

    setEditCourseLoading(true);

    try {
      await axios.put(`${API_URL}/courses/${selectedCourse.id}`, courseForm);
      showSnackbar('Course updated successfully!', 'success');
      setEditDialogOpen(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update course';
      showSnackbar(errorMessage, 'error');
    } finally {
      setEditCourseLoading(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourse) return;

    setDeleteCourseLoading(true);

    try {
      await axios.delete(`${API_URL}/courses/${selectedCourse.id}`);
      showSnackbar('Course deleted successfully!', 'success');
      setDeleteDialogOpen(false);
      setSelectedCourse(null);
      fetchCourses();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete course';
      showSnackbar(errorMessage, 'error');
    } finally {
      setDeleteCourseLoading(false);
    }
  };

  const resetForm = () => {
    setCourseForm({ name: '' });
    setErrors({});
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    // Reset to first page when searching
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    fetchCourses();
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const openAddDialog = () => {
    resetForm();
    setAddDialogOpen(true);
  };

  const openEditDialog = (course) => {
    setSelectedCourse(course);
    setCourseForm({ name: course.name });
    setErrors({});
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (course) => {
    setSelectedCourse(course);
    setDeleteDialogOpen(true);
  };

  const columns = [
    { field: 'name', headerName: 'Course Name', width: 300 },
    { 
      field: 'createdAt', 
      headerName: 'Created Date', 
      width: 200,
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleDateString();
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit Course">
            <IconButton
              size="small"
              onClick={() => openEditDialog(params.row)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Course">
            <IconButton
              size="small"
              onClick={() => openDeleteDialog(params.row)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const rows = courses.map(course => ({
    id: course._id,
    name: course.name,
    createdAt: course.createdAt,
  }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Course Management
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSearch}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search by Course Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setSearchTerm('');
                  fetchCourses();
                }}
              >
                Clear
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Tooltip title="Add new course">
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                  onClick={openAddDialog}
                >
                  Add Course
                </Button>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={1}>
              <Tooltip title="Course Management">
                <IconButton size="small" color="info">
                  <CourseIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={totalCourses}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection
          disableSelectionOnClick
          loading={loading}
          getRowId={(row) => row.id}
        />
      </Paper>
      
      {/* Add Course Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add New Course
          <IconButton onClick={() => setAddDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Course Name"
                value={courseForm.name}
                onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAddCourse}
            variant="contained"
            disabled={addCourseLoading}
            startIcon={addCourseLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {addCourseLoading ? 'Adding...' : 'Add Course'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Course Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Course
          <IconButton onClick={() => setEditDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Course Name"
                value={courseForm.name}
                onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleEditCourse}
            variant="contained"
            disabled={editCourseLoading}
            startIcon={editCourseLoading ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {editCourseLoading ? 'Updating...' : 'Update Course'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Course Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Delete Course
          <IconButton onClick={() => setDeleteDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to delete the course "{selectedCourse?.name}"? 
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCourse}
            variant="contained"
            color="error"
            disabled={deleteCourseLoading}
            startIcon={deleteCourseLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteCourseLoading ? 'Deleting...' : 'Delete Course'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CourseManagement;